import React from "react";
import { CreditCard, Banknote, Undo2, Zap, Lock, Edit3, Trash2 } from "lucide-react";

// Transaction type config
const TRANSACTION_CONFIG = {
  stripe: { icon: Zap, label: "Online Payment" },
  card_pos: { icon: CreditCard, label: "Card (POS)" },
  cash: { icon: Banknote, label: "Cash" },
  refund: { icon: Undo2, label: "Refund" }
};

const REFUND_REASONS = {
  route_change: "Route Change",
  cancellation: "Cancellation",
  price_adjustment: "Price Adjustment",
  other: "Other"
};

// Format date
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatTime(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// Payment Summary Card - Matching existing admin card style
export function PaymentSummaryCard({ summary, compact = false }) {
  if (!summary) return null;
  
  const statusStyles = {
    unpaid: "bg-red-100 text-red-800",
    partial: "bg-yellow-100 text-yellow-800",
    paid: "bg-green-100 text-green-800",
    refunded: "bg-gray-100 text-gray-800"
  };

  if (compact) {
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[summary.payment_status] || "bg-gray-100 text-gray-800"}`}>
        {summary.payment_status} {summary.balance > 0 && `• €${summary.balance} due`}
      </span>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">Payment Summary</h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[summary.payment_status]}`}>
          {summary.payment_status}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Total Price</p>
          <p className="text-xl font-bold text-gray-900">€{summary.total_price}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Net Paid</p>
          <p className="text-xl font-bold text-green-600">€{summary.net_paid}</p>
        </div>
      </div>
      
      <div className="border-t pt-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Payments</span>
          <span className="text-green-600">+€{summary.total_payments}</span>
        </div>
        {summary.total_refunds > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Refunds</span>
            <span className="text-red-600">-€{summary.total_refunds}</span>
          </div>
        )}
        <div className="flex justify-between font-medium pt-1 border-t border-dashed">
          <span className="text-gray-700">{summary.balance > 0 ? "Balance Due" : summary.balance < 0 ? "Credit" : "Balance"}</span>
          <span className={summary.balance > 0 ? "text-orange-600" : summary.balance < 0 ? "text-orange-600" : "text-green-600"}>
            €{Math.abs(summary.balance)}
          </span>
        </div>
      </div>
      
      {summary.has_overpayment && (
        <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700">
          Customer has €{Math.abs(summary.balance)} credit
        </div>
      )}
    </div>
  );
}

// Transaction row - simple table-like style
function TransactionRow({ transaction, isAdmin, onEdit, onDelete }) {
  const config = TRANSACTION_CONFIG[transaction.type] || TRANSACTION_CONFIG.cash;
  const Icon = config.icon;
  const isRefund = transaction.type === "refund";
  
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-b-0">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-600" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 text-sm">{config.label}</span>
          {transaction.is_stripe && (
            <Lock className="w-3 h-3 text-gray-400" title="Stripe payment (locked)" />
          )}
        </div>
        <div className="text-xs text-gray-500">
          {formatDate(transaction.created_at)} • {formatTime(transaction.created_at)}
          {transaction.refund_reason && (
            <span className="text-orange-600"> • {REFUND_REASONS[transaction.refund_reason]}</span>
          )}
        </div>
        {transaction.note && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{transaction.note}</p>
        )}
      </div>
      
      <div className={`font-medium ${isRefund ? "text-red-600" : "text-green-600"}`}>
        {isRefund ? "-" : "+"}€{transaction.amount}
      </div>
      
      {isAdmin && !transaction.is_stripe && (
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(transaction)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
            title="Edit"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(transaction)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// Transaction History List
export function TransactionHistory({ 
  transactions = [], 
  isAdmin = false, 
  onEdit, 
  onDelete,
  emptyMessage = "No transactions yet"
}) {
  if (transactions.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        {emptyMessage}
      </div>
    );
  }
  
  return (
    <div>
      {transactions.map((txn) => (
        <TransactionRow
          key={txn.transaction_id}
          transaction={txn}
          isAdmin={isAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export { TRANSACTION_CONFIG, REFUND_REASONS };
export default TransactionHistory;
